import { action, observable } from 'mobx';
import { FormViewModelController, FormVmValueType } from '../../../../../../infrastructure/form-viewmodel-controller';
import { ConnectionFields } from '../../../../../../modules/connections/config';

const NO_WHITESPACE_OR_SLASH = /^[^\/\s]+$/;

/*
 * Keeps a link input built on top of the Embed Code ID in sync with the
 * ConnectionFields.EMBED_CODE_ID form value in both directions.
 * Subclasses only have to provide the link prefix.
 */
export abstract class EmbedCodeLinkViewModel {
    public static isValidUrl(url: string): boolean {
        try {
            return !!(new URL(url));
        } catch {
            return false;
        }
    }

    public static normalizeEmbedCode(code: string): string {
        return (code || '').split('?')[0].trim();
    }

    public getValue(): string {
        this.syncWithEmbedCodeInputValue();
        return this.rawValue;
    }

    @action
    public setValue(url: string): void {
        this.rawValue = this.normalizeLink(url);
        this.syncWithInputValue();
    }

    public get isValid(): boolean {
        return !!(
            this.rawValue &&
            this.hasValidFormat() &&
            NO_WHITESPACE_OR_SLASH.test(this.extractEmbedCode())
        );
    }

    constructor(protected readonly vmFormCtrl: FormViewModelController) {}

    protected abstract get linkPrefix(): string;

    @observable
    private rawValue: string = '';

    public matchesLinkFormat(url: string): boolean {
        return EmbedCodeLinkViewModel.isValidUrl(url) && url.startsWith(this.linkPrefix);
    }

    public buildLinkById(id: string): string {
        return id ? this.linkPrefix + id : '';
    }

    private normalizeLink(url: string): string {
        if (this.matchesLinkFormat(url)) {
            const embedCode = url.replace(this.linkPrefix, '');
            const normalizedEmbedCode = EmbedCodeLinkViewModel.normalizeEmbedCode(embedCode);

            return this.linkPrefix + normalizedEmbedCode;
        }

        return url;
    }

    private syncWithInputValue(): void {
        if (this.isOutOfSync) {
            this.setEmbedCodeInputValue(this.extractEmbedCode());
        }
    }

    private syncWithEmbedCodeInputValue(): void {
        if (this.isNotSet || this.isOutOfSync) {
            this.setValue(this.buildLinkById(this.embedCodeInputValue as string));
        }
    }

    private get isNotSet(): boolean {
        return !!(this.embedCodeInputValue && !this.rawValue);
    }

    private get isOutOfSync(): boolean {
        return !!(this.hasValidFormat() && this.embedCodeInputValue !== this.extractEmbedCode());
    }

    private extractEmbedCode(): string | null {
        return this.hasValidFormat() ? this.rawValue.replace(this.linkPrefix, '') : null;
    }

    private hasValidFormat(): boolean {
        return this.matchesLinkFormat(this.rawValue);
    }

    private setEmbedCodeInputValue(value: string): void {
        this.vmFormCtrl.setValue(ConnectionFields.EMBED_CODE_ID, value);
    }

    private get embedCodeInputValue(): FormVmValueType {
        return this.vmFormCtrl.getValue(ConnectionFields.EMBED_CODE_ID) as string;
    }
}
